(function (global) {
  'use strict';

  global.NewHomeSource = global.NewHomeSource || {};
  global.NewHomeSource.Carousel = global.NewHomeSource.Carousel || {};

  const Carousel = global.NewHomeSource.Carousel;

  if (!Carousel.EventStore ||
    !Carousel.Limit ||
    !Carousel.ScrollTarget ||
    !Carousel.DragTracker) {
    return;
  }

  class DragHandler {
    /**
     * Initializes a new instance of the drag handler for the carousel.
     *
     * @constructor
     * @property {any} state - The current state of the drag handler.
     * @property {boolean} isMoving - Indicates if a drag movement is in progress.
     * @property {number} startScroll - The initial scroll position when dragging starts.
     * @property {number} startCross - The initial cross-axis position when dragging starts.
     * @property {boolean} pointerIsDown - True if the pointer is currently pressed down.
     * @property {boolean} preventScroll - True if scrolling should be prevented during drag.
     * @property {boolean} preventClick - True if click events should be prevented after drag.
     * @property {boolean} isMouse - True if the current pointer is a mouse.
     * @property {string[]} focusNodes - List of HTML node names that can receive focus.
     * @property {Object} nonPassiveEvent - Event options for non-passive listeners.
     * @property {Carousel.EventStore} initEvents - Stores initial event listeners.
     * @property {Carousel.EventStore} dragEvents - Stores drag event listeners.
     * @property {Object} snapForceBoost - Force boost values for snap mode by pointer type.
     * @property {Object} freeForceBoost - Force boost values for free mode by pointer type.
     */
    constructor() {
      this.state = null;
      this.isMoving = false;
      this.startScroll = 0;
      this.startCross = 0;
      this.pointerIsDown = false;
      this.preventScroll = false;
      this.preventClick = false;
      this.isMouse = false;
      this.focusNodes = ['INPUT', 'SELECT', 'TEXTAREA'];
      this.nonPassiveEvent = { passive: false };
      this.initEvents = new Carousel.EventStore();
      this.dragEvents = new Carousel.EventStore();
      this.snapForceBoost = { mouse: 300, touch: 400 };
      this.freeForceBoost = { mouse: 500, touch: 600 };
      this.minLimitToNextThreshold = 50;
      this.maxLimitToNextThreshold = 225;
    }

    /**
     * Initializes the drag handler for the carousel component.
     *
     * Sets up internal state, calculates thresholds, and attaches event listeners
     * for drag and touch interactions on the carousel root node. Also initializes
     * supporting objects for scroll and drag tracking.
     *
     * @param {Object} state - The state object containing configuration and references
     *   for the carousel, including:
     *   - {Function} get: Retrieves values by key from the state.
     *   - {Object} percentOfView: Utility for measuring view percentages.
     *   - {boolean} watchDrag: Whether to enable drag event handling.
     *   - {HTMLElement} rootNode: The root DOM node for the carousel.
     *   - {boolean} dragFree: Whether the carousel is in drag-free mode.
     *   - {number} friction: The friction value for drag movement.
     */
    initialize(state) {
      this.state = state;
      const percentOfView = this.state.get('percentOfView');
      const limit = new Carousel.Limit();
      limit.initialize(this.minLimitToNextThreshold, this.maxLimitToNextThreshold);
      this.goToNextThreshold = limit.constrain(percentOfView.measure(20));
      const watchDrag = this.state.get('watchDrag');
      const node = this.state.get('container');
      const dragFree = this.state.get('dragFree');
      this.baseSpeed = dragFree ? 43 : 25;
      this.scrollTarget = new Carousel.ScrollTarget();
      this.scrollTarget.initialize(this.state);
      this.dragTracker = new Carousel.DragTracker();
      this.dragTracker.initialize(this.state);
      this.baseFriction = this.state.get('friction');

      if (!watchDrag) {
        return;
      }

      this.initEvents
        .add(node, 'dragstart', (evt) => { evt.preventDefault(); }, this.nonPassiveEvent)
        .add(node, 'touchmove', () => undefined, this.nonPassiveEvent)
        .add(node, 'touchend', () => undefined)
        .add(node, 'touchstart', this.down.bind(this))
        .add(node, 'mousedown', this.down.bind(this))
        .add(node, 'touchcancel', this.up.bind(this))
        .add(node, 'contextmenu', this.up.bind(this))
        .add(node, 'click', this.click.bind(this), true);
    }

    /**
     * Cleans up event listeners by clearing all initialized and drag-related events.
     * Should be called to prevent memory leaks when the drag handler is no longer needed.
     */
    destroy() {
      this.initEvents.clear();
      this.dragEvents.clear();
    }

    /**
     * Determines if the given DOM node is considered a focusable node.
     *
     * @param {Node} node - The DOM node to check.
     * @returns {boolean} True if the node's name is in the list of focusable nodes, otherwise false.
     */
    isFocusNode(node) {
      const nodeName = node.nodeName || '';
      return this.focusNodes.indexOf(nodeName) !== -1;
    }

    /**
     * Calculates and returns the appropriate force boost value based on the current drag state and input type.
     *
     * @returns {number} The force boost value for the current drag mode and input type.
     */
    forceBoost() {
      const dragFree = this.state.get('dragFree');
      const boost = dragFree ? this.freeForceBoost : this.snapForceBoost;
      const type = this.isMouse ? 'mouse' : 'touch';
      return boost[type];
    }

    /**
     * Calculates the allowed scroll force based on the current carousel state and user interaction.
     *
     * @param {number} force - The attempted scroll force (positive or negative).
     * @param {boolean} targetChanged - Indicates if the target slide has changed during the drag.
     * @returns {number} The adjusted scroll force that is allowed, considering drag mode,
     * thresholds, and snap skipping.
     */
    allowedForce(force, targetChanged) {
      const index = this.state.get('index');
      const dragFree = this.state.get('dragFree');
      const skipSnaps = this.state.get('skipSnaps');
      const next = index.add(Math.sign(force) * -1);
      const baseForce = this.scrollTarget.byDistance(force, !dragFree).distance;

      if (dragFree || Math.abs(force) < this.goToNextThreshold) {
        return baseForce;
      }

      if (skipSnaps && targetChanged) {
        return baseForce * 0.5;
      }

      return this.scrollTarget.byIndex(next.get(), 0).distance;
    }

    /**
     * Handles the pointer up event for the carousel drag interaction.
     * Calculates the force and speed of the scroll based on the drag,
     * updates the carousel state, and initiates scrolling to the target.
     *
     * @param {PointerEvent|MouseEvent|TouchEvent} evt - The event object from the pointer up action.
     */
    up(evt) {
      const index = this.state.get('index');
      const axis = this.state.get('axis');
      const dragFree = this.state.get('dragFree');
      const currentLocation = this.scrollTarget.byDistance(0, false);
      const targetChanged = currentLocation.index !== index.get();
      const rawForce = this.dragTracker.pointerUp(evt) * this.forceBoost();
      const force = this.allowedForce(axis.direction(rawForce), targetChanged);
      const forceFactor = DragHandler.factorAbs(rawForce, force);
      const speed = this.baseSpeed - 10 * forceFactor;
      const friction = this.baseFriction + forceFactor / 50;

      this.preventScroll = false;
      this.pointerIsDown = false;
      this.dragEvents.clear();
      this.state.setDuration(speed);
      this.state.setFriction(friction);
      const data = {
        number: force,
        snap: !dragFree
      };
      this.state.notifyObservers('scrollByDistance', data);
      if (this.preventClick) {
        this.state.notifyObservers('carouselNavigation', {
          originator: 'dragNavigation',
        });
      }
      this.isMouse = false;
    }

    /**
     * Handles the drag move event for the carousel.
     *
     * @param {MouseEvent | TouchEvent} evt - The event object from the drag action.
     *
     * @description
     * This method processes pointer movement during a drag operation. It determines the axis of movement,
     * checks for multi-touch gestures, calculates the distance moved along the main and cross axes,
     * and decides whether to prevent scrolling or allow it. If the drag exceeds a threshold, it prevents
     * click events and updates the carousel's state, including animation duration and friction.
     * It also notifies observers of animation start and updates the target position.
     * The default browser behavior is prevented to ensure smooth dragging.
     */
    move(evt) {
      const axis = this.state.get('axis');
      const crossAxis = axis.getCross();
      const dragThreshold = this.state.get('dragThreshold');
      const target = this.state.get('target');

      const isTouchEvt = !(evt instanceof global.MouseEvent);
      if (isTouchEvt && evt.touches.length >= 2) {
        return this.up(evt);
      }

      const lastScroll = this.dragTracker.readPoint(evt);
      const lastCross = this.dragTracker.readPoint(evt, crossAxis);
      const diffScroll = Math.abs(lastScroll - this.startScroll);
      const diffCross = Math.abs(lastCross - this.startCross);

      if (!this.preventScroll && !this.isMouse) {
        if (!evt.cancelable) {
          return this.up(evt);
        }

        this.preventScroll = diffScroll > diffCross;
        if (!this.preventScroll) {
          return this.up(evt);
        }
      }

      const diff = this.dragTracker.pointerMove(evt);
      if (diffScroll > dragThreshold) {
        this.preventClick = true;
      }

      this.state.setDuration(0.75);
      this.state.setFriction(0.3);
      this.state.notifyObservers('startAnimation');
      target.add(axis.direction(diff));
      evt.preventDefault();
      return undefined;
    }

    /**
     * Handles click events to prevent unintended clicks after a drag operation.
     * If `preventClick` is true, the event's propagation and default behavior are stopped,
     * and `preventClick` is reset to false.
     *
     * @param {MouseEvent} evt - The click event object.
     */
    click(evt) {
      if (this.preventClick) {
        evt.stopPropagation();
        evt.preventDefault();
        this.preventClick = false;
      }
    }

    /**
     * Checks if the pointer is currently pressed down.
     * @returns {boolean} True if the pointer is down, otherwise false.
     */
    pointerDown() {
      return this.pointerIsDown;
    }

    /**
     * Attaches drag-related event listeners (touch and mouse) to the appropriate DOM node.
     * The method binds move and up event handlers for both touch and mouse interactions,
     * ensuring drag functionality works across different input types.
     *
     * @returns {void}
     */
    addDragEvents() {
      const node = this.isMouse ? document : this.state.get('container');
      this.dragEvents
        .add(node, 'touchmove', this.move.bind(this), this.nonPassiveEvent)
        .add(node, 'touchend', this.up.bind(this))
        .add(node, 'mousemove', this.move.bind(this), this.nonPassiveEvent)
        .add(node, 'mouseup', this.up.bind(this));
    }

    /**
     * Handles the pointer down event for initiating a drag operation on the carousel.
     *
     * @param {MouseEvent | TouchEvent} evt - The event object representing the pointer down action.
     * @returns {void}
     */
    down(evt) {
      const isMouseEvt = evt instanceof global.MouseEvent;
      const dragFree = this.state.get('dragFree');
      const target = this.state.get('target');
      const location = this.state.get('location');
      const crossAxis = this.state.get('axis').getCross();
      this.isMouse = isMouseEvt;
      this.preventClick = dragFree && isMouseEvt && !evt.buttons && this.isMoving;
      this.isMoving = Math.abs(target.get() - location.get()) >= 2;

      if (isMouseEvt && evt.button !== 0) { return; }
      if (this.isFocusNode(evt.target)) {
        return;
      }

      this.pointerIsDown = true;
      this.dragTracker.pointerDown(evt);
      this.state.setDuration(0);
      this.state.setFriction(0);
      target.set(location.get());
      this.addDragEvents();
      this.startScroll = this.dragTracker.readPoint(evt);
      this.startCross = this.dragTracker.readPoint(evt, crossAxis);
    }

    /**
     * Calculates the normalized absolute difference between two values.
     *
     * Returns 0 if either value is 0, or if the absolute value of valueB is less than or equal to valueA.
     * Otherwise, returns the absolute difference between the absolute values of valueB and valueA,
     * divided by valueB.
     *
     * @param {number} valueB - The second value to compare.
     * @param {number} valueA - The first value to compare.
     * @returns {number} The normalized absolute difference, or 0 based on the conditions.
     */
    static factorAbs(valueB, valueA) {
      if (valueB === 0 || valueA === 0) {
        return 0;
      }

      if (Math.abs(valueB) <= Math.abs(valueA)) {
        return 0;
      }

      const diff = Math.abs(Math.abs(valueB) - Math.abs(valueA));
      return Math.abs(diff / valueB);
    }
  }

  global.NewHomeSource.Carousel.DragHandler = DragHandler;
}(window));
